/**
 * PlanBadge — header pill showing the user's current plan ("Achiever", "School · DPS Noida"), or a
 * "Trial · N days left" pill during a trial. Staff and unloaded entitlements show nothing.
 */

import React from 'react';
import { useEntitlements } from '@context/EntitlementContext';
import { useAuth } from '@context/AuthContext';
import { isParentUser } from '@services/api/parent.service';

export const PlanBadge: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { entitlements, trialDaysLeft } = useEntitlements();
  const { user } = useAuth();
  // A parent account has no plan of its own (plans belong to the children; see Payments), so no "Free · Upgrade" pill.
  if (isParentUser(user as any)) return null;
  if (!entitlements || entitlements.source === 'staff') return null;

  let text: string;
  let tone: string;
  if (entitlements.source === 'trial') {
    text = trialDaysLeft === 0 ? 'Trial ends today' : `Trial · ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`;
    tone = (trialDaysLeft ?? 0) <= 3
      ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
      : 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  } else if (entitlements.source === 'school') {
    text = entitlements.school_name ? `School · ${entitlements.school_name}` : 'School plan';
    tone = 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  } else if (entitlements.source === 'free') {
    text = 'Free · Upgrade';
    tone = 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30';
  } else {
    text = entitlements.plan_name;
    tone = 'bg-violet-500/15 text-violet-300 border-violet-500/30';
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title="Your plan — see plans"
      className={`hidden sm:inline-flex items-center gap-1 max-w-[200px] truncate px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-opacity hover:opacity-80 ${tone}`}
    >
      <span aria-hidden>{entitlements.source === 'trial' ? '⏳' : entitlements.source === 'school' ? '🏫' : '⭐'}</span>
      <span className="truncate">{text}</span>
    </button>
  );
};

export default PlanBadge;
